import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Plus, Trash2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", subtitle: "", image_url: "", cta_label: "", cta_link: "", position: "hero", sort_order: 0, is_active: true };

export default function Banners() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState(EMPTY);

  const load = () => api.get("/cms/banners").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit(null); setF(EMPTY); setOpen(true); };
  const openEdit = (b) => { setEdit(b); setF({ ...EMPTY, ...b }); setOpen(true); };

  const save = async () => {
    if (!f.title || !f.image_url) { toast.error("Title and image URL required"); return; }
    try {
      if (edit) await api.patch(`/cms/banners/${edit.id}`, f);
      else await api.post("/cms/banners", f);
      toast.success("Banner saved");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (b) => {
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    try { await api.delete(`/cms/banners/${b.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="banners-page">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Content CMS</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Storefront Banners</h1>
          <p className="text-sm text-muted-foreground mt-1">Hero and secondary content displayed on furmigo.shop.</p>
        </div>
        <Button onClick={openNew} data-testid="add-banner-btn"><Plus className="w-4 h-4 mr-1" />New banner</Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {rows.map(b => (
          <Card key={b.id} className="overflow-hidden" data-testid={`banner-${b.id}`}>
            <div className="h-32 bg-stone-100 relative">
              {b.image_url ? <img src={b.image_url} alt={b.title} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-stone-300"><ImageIcon className="w-10 h-10" /></div>}
              {!b.is_active && <Badge variant="destructive" className="absolute top-2 right-2">Inactive</Badge>}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-heading text-lg font-bold">{b.title}</div>
                  {b.subtitle && <div className="text-xs text-muted-foreground">{b.subtitle}</div>}
                </div>
                <Badge variant="outline" className="text-[10px]">{b.position}</Badge>
              </div>
              <div className="mt-2 text-xs text-muted-foreground">CTA: {b.cta_label || "—"} → {b.cta_link || "—"}</div>
              <div className="mt-3 flex justify-end gap-1">
                <Button variant="outline" size="sm" onClick={() => openEdit(b)}>Edit</Button>
                <Button variant="ghost" size="sm" onClick={() => del(b)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </Card>
        ))}
        {rows.length === 0 && <div className="col-span-full text-sm text-muted-foreground py-6 text-center">No banners yet. Add your first to liven up the storefront home.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="banner-dialog">
          <DialogHeader><DialogTitle>{edit ? "Edit banner" : "New banner"}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Title *</Label>
            <Input value={f.title} onChange={e => setF(p => ({ ...p, title: e.target.value }))} data-testid="banner-title" />
            <Label className="text-xs">Subtitle</Label>
            <Input value={f.subtitle} onChange={e => setF(p => ({ ...p, subtitle: e.target.value }))} />
            <Label className="text-xs">Image URL *</Label>
            <Input value={f.image_url} onChange={e => setF(p => ({ ...p, image_url: e.target.value }))} placeholder="https://…/image.jpg" data-testid="banner-image" />
            {f.image_url && <img src={f.image_url} alt="preview" className="w-full h-32 object-cover rounded-md mt-1" />}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">CTA label</Label>
                <Input value={f.cta_label} onChange={e => setF(p => ({ ...p, cta_label: e.target.value }))} placeholder="Shop now" />
              </div>
              <div>
                <Label className="text-xs">CTA link</Label>
                <Input value={f.cta_link} onChange={e => setF(p => ({ ...p, cta_link: e.target.value }))} placeholder="/products" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Position</Label>
                <Select value={f.position} onValueChange={v => setF(p => ({ ...p, position: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hero">Hero (homepage carousel)</SelectItem>
                    <SelectItem value="secondary">Secondary</SelectItem>
                    <SelectItem value="footer">Footer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Sort order</Label>
                <Input type="number" value={f.sort_order} onChange={e => setF(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm pt-1">
              <input type="checkbox" checked={f.is_active} onChange={e => setF(p => ({ ...p, is_active: e.target.checked }))} />
              Active (visible on storefront)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="banner-save">{edit ? "Save changes" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
